import {
  FormEnumFieldWidget,
  FormFieldWidget,
  http,
  ModelFieldType,
  RuntimeEnumerationOption,
  SPI,
  ViewType,
  Widget,
  CallChaining
} from '@kunlun/dependencies';
import { Message } from '../../../../constants/message';

type EnumerationValue = boolean | string;

@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.Enum,
    widget: 'BizTypeSelect',
    multi: false
  })
)
export class FormEnumBizTypeSelectFieldWidget extends FormEnumFieldWidget {
  public initialize(props) {
    super.initialize(props);
    return this;
  }

  @Widget.Reactive()
  selectOptions: RuntimeEnumerationOption[] = [];

  @Widget.Reactive()
  protected get options(): RuntimeEnumerationOption[] {
    return this.selectOptions;
  }

  @Widget.Reactive()
  @Widget.Inject()
  protected mountedCallChaining: CallChaining | undefined;

  protected mounted() {
    super.mounted();
    if (this.mountedCallChaining) {
      this.mountedCallChaining.hook(this.path, async () => {
        await this.mountedProcess();
      });
    } else {
      this.mountedProcess();
    }
  }

  private watchLock = true;

  protected mountedProcess(): void {
    this.watchLock = false;
  }

  @Widget.Watch('formData.orderType')
  protected watchOrderType(value: string) {
    if (value) {
      this.getBizTypeByOrderType(value);
    } else {
      this.selectOptions = [];
    }
    if (!this.formData.bizType) {
      // 默认常规订货
      this.setValue('REGULAR_ORDERING');
    }
  }

  @Widget.Watch('formData.bizType')
  protected watchBizType(value: string) {
    if (this.watchLock) {
      return;
    }
    if (value) {
      this.getDistributionChannelByBizType(value);
    } else {
      this.formData.distributionChannel = null;
    }
    //业务类型切换清空推广方案
    this.formData.deliAriesPromotionPlan = null;
  }

  async getDistributionChannelByBizType(bitType: string) {
    if (!this.formData?.sellerPartnerRelationApplySearch) {
      return;
    }
    const gql = `
      mutation {
        sellerDistributionCreateOrderParamMutation {
          bizTypeConstructMirror(
            data: {
              orderType: ${this.formData.orderType}
              bizType: ${bitType}
              sellerPartnerRelationApplySearch: {
                searchPartnerRelationApplyRes: "${
                  (this.formData.sellerPartnerRelationApplySearch as Record<any, any>).searchPartnerRelationApplyRes
                }"
                id: ${(this.formData.sellerPartnerRelationApplySearch as Record<any, any>).id}
              }
            }
          ) {
            distributionChannel {
              id
              name
            }
            deliveryAddressCopy {
              labelTitle
              id
            }
            deliveryAddress {
              address {
                id
                street2
                fullAddress
                countryCode
                countryName
                provinceCode
                provinceName
                cityCode
                cityName
                districtName
                districtCode
                streetCode
                streetName
              }
            }
          }
        }
      }
    `;
    try {
      const res = await http.query(this.model.moduleName, gql);
      const resData = res?.data?.sellerDistributionCreateOrderParamMutation?.bizTypeConstructMirror;
      this.formData.distributionChannel = resData?.distributionChannel ?? null;
      this.formData.deliveryAddressCopy = resData?.deliveryAddressCopy ?? null;
      this.formData.deliveryAddress = resData?.deliveryAddress ?? null;
      this.reloadFormData$.subject.next(true);
    } catch (e) {
      this.formData.distributionChannel = null;
      this.formData.deliveryAddressCopy = null;
      this.formData.deliveryAddress = null;
      this.reloadFormData$.subject.next(true);
    }
  }

  private buyerIdCacheFunctionList: Array<() => any> = [];

  async getBizTypeByOrderType(orderType: string): Promise<void> {
    this.selectOptions = [];
    const res = (await this.queryBizType(orderType)) as Record<string, string>;
    this.selectOptions = Object.keys(res).map((k) => ({ name: k, displayName: res[k], value: k }));
    if (!this.selectOptions.some((option) => option.value === this.formData.bizType)) {
      const defaultTarget =
        this.selectOptions.length && this.selectOptions.some((option) => option.value === 'REGULAR_ORDERING')
          ? 'REGULAR_ORDERING'
          : this.selectOptions[0].value;
      this.setValue(defaultTarget);
    }
  }

  async queryBizType(orderType) {
    if (!this.formData.buyerPartnerId) {
      this.buyerIdCacheFunctionList.push(() => this.getBizTypeByOrderType(orderType));
      throw new Error('buyerPartnerId not found');
    }
    const gql = `
      mutation {
        sellerDistributionCreateOrderParamMutation {
          fetchBizType(data: { 
            orderType: ${orderType}
            buyerPartnerId: ${this.formData.buyerPartnerId}
          }) {
            bizType
          }
        }
      }
    `;

    const res = await http.query(this.model.moduleName, gql);

    return res.data.sellerDistributionCreateOrderParamMutation.fetchBizType.bizType;
  }

  @Widget.Watch('formData.buyerPartnerId')
  private async watchBuyerPartnerId(val) {
    if (val) {
      this.buyerIdCacheFunctionList.forEach((fn) => {
        fn();
      });
      this.buyerIdCacheFunctionList = [];
    }
  }

  change(val: EnumerationValue | null | undefined): void {
    super.change(val);
    window.postMessage(Message.ChangeBizType);
  }
}
